import React, { FC, useCallback, useMemo, useState } from 'react';
import { Box, Button, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { DiscountTypeEnum } from '@sweatpals/shared';
import { isNumber } from 'lodash-es';
import { Colors } from '../../../constants';
import { DiscountsFormDto, DiscountTierEnum } from '../../../models/dto';
import { TextField } from '../../inputs';
import { useCustomRegister } from '../../../hooks';
import { CoreRadio, CoreRadioGroup, RadioFormLabel } from '../../_core/core-radio';
import { Showable } from '../../utils';
import { CoreConditionalSelect, CoreLabeledCheckbox, CoreNumberInput, Option } from '../../_core';
import { defaultSelectInputStyles } from '../../inputs/input.styles';
import { Card } from '../../card';
import { InfoIcon, LimitIcon } from '../../../icons';
import { CollapseTriggerWithIcon } from '../../collapse-trigger-with-icon/collapse-trigger-with-icon.component';

export interface PromocodeCardProps {
	index: number;
	tiersOptions: Option[];
	promocodeUses?: number;
	hideDeleteButton?: boolean;
	tiersLabel?: string;
	allTiersRadioLabel?: string;
	selectedTiersRadioLabel?: string;
	onRemove(): void;
}

export const PromocodeCard: FC<PromocodeCardProps> = (props) => {
	const {
		index,
		promocodeUses,
		hideDeleteButton,
		tiersOptions,
		tiersLabel,
		allTiersRadioLabel,
		selectedTiersRadioLabel,
		onRemove,
	} = props;

	const [advancedSettingsIsVisible, setAdvancedSettingsIsVisible] = useState(false);

	const form = useFormContext<DiscountsFormDto>();
	const { watch, setValue, formState, trigger } = form;

	const { register } = useCustomRegister(form);

	const errors = formState.errors?.discounts?.[index] ?? {};

	const type = watch(`discounts.${index}.type`);
	const tiersType = watch(`discounts.${index}.tiersType`);
	const withMaxUses = watch(`discounts.${index}.withMaxUses`);
	const maxUses = watch(`discounts.${index}.maxUses`);
	const isFixed = useMemo(() => type === DiscountTypeEnum.Fixed, [type]);

	const onChangeType = useCallback(
		(newType: DiscountTypeEnum) => {
			setValue(`discounts.${index}.type`, newType);

			// by now we just reset the value
			setValue(`discounts.${index}.amount`, null);
			trigger(`discounts.${index}.amount`);
		},
		[index, setValue, trigger]
	);

	return (
		<Card sx={{ p: 0 }} onDelete={hideDeleteButton ? undefined : onRemove}>
			<Box p={isNumber(promocodeUses) ? '16px 16px 0' : '16px'}>
				<Box display="flex" alignItems="baseline" gap="8px">
					<TextField
						{...register(`discounts.${index}.code`, {
							transformValue: (value?: string): string | undefined => value?.toLowerCase()?.replace(/\s/g, ''),
						})}
						label="Promo code name"
						placeholder="i.e. SWEATPALS10"
					/>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'flex-end',
							gap: '4px',
							flex: 1,
						}}
					>
						<CoreNumberInput
							{...(register(`discounts.${index}.amount`, {
								type: 'number',
								passRef: false,
								passType: false,
								min: 0,
								max: type === DiscountTypeEnum.Percentage ? 100 : undefined,
								step: type === DiscountTypeEnum.Percentage ? 1 : 0.01,
								digits: type === DiscountTypeEnum.Percentage ? 0 : 2,
								transformValue: (v?: number) => (v && isFixed ? Number(v) / 100 : v),
							}) as any)}
							onChange={value => {
								const newValue = isFixed && value ? Math.round(value * 100) : value;
								form.setValue(`discounts.${index}.amount`, newValue);
							}}
							lang="en-US"
							label="Discount"
							placeholder="10"
							fullWidth
							allowNegative={false}
							decimalScale={isFixed ? 2 : 0}
							isAllowed={({ floatValue }) => !floatValue || isFixed || (floatValue >= 0 && floatValue <= 100)}
						/>

						<Box
							id={`discounts.${index}.type`}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '3px',
								p: '2px 6px 3px 6px',
								borderRadius: '8px',
								backgroundColor: Colors.white_a10,
								mb: errors?.amount ? '25px' : 'unset',
							}}
						>
							<Button
								onClick={() => onChangeType(DiscountTypeEnum.Fixed)}
								sx={{
									p: '5px 10px',
									minWidth: 'unset',
									backgroundColor: type === DiscountTypeEnum.Fixed ? Colors.white_a10 : Colors.transparent,
									border: `1px solid ${type === DiscountTypeEnum.Fixed ? Colors.white_a40 : Colors.transparent}`,
									borderRadius: '8px',
									color: Colors.white,
									minHeight: 'unset',
									maxHeight: '40px',
								}}
							>
								$
							</Button>
							<Button
								onClick={() => onChangeType(DiscountTypeEnum.Percentage)}
								sx={{
									p: '5px 10px',
									minWidth: 'unset',
									backgroundColor: type === DiscountTypeEnum.Percentage ? Colors.white_a10 : Colors.transparent,
									border: `1px solid ${type === DiscountTypeEnum.Percentage ? Colors.white_a40 : Colors.transparent}`,
									borderRadius: '8px',
									color: Colors.white,
									minHeight: 'unset',
									maxHeight: '40px',
								}}
							>
								%
							</Button>
						</Box>
					</Box>
				</Box>

				<Box pt="22px">
					<CollapseTriggerWithIcon
						id={`discounts.${index}.advanced-settings`}
						isActivated={advancedSettingsIsVisible}
						setIsActivated={setAdvancedSettingsIsVisible}
					/>
				</Box>

				<Collapse in={advancedSettingsIsVisible}>
					<Box display="flex" flexDirection="column" gap="10px" pt="10px">
						<CoreRadioGroup
							{...register(`discounts.${index}.tiersType`, {
								type: 'radio',
							})}
							controlId={`discounts.${index}.tiersType`}
							onChange={(_e, value: string) => {
								setValue(`discounts.${index}.tiersType`, value as DiscountTierEnum);

								if (value === DiscountTierEnum.All) {
									setValue(`discounts.${index}.tiers`, { all: true });
								} else {
									setValue(`discounts.${index}.tiers`, {});
								}
							}}
							row
							label={tiersLabel ?? 'Applicable to:'}
							controlSx={{
								'& .MuiFormControlLabel-root:not(:last-child)': {
									marginRight: '24px',
								},
							}}
						>
							<RadioFormLabel
								label={allTiersRadioLabel ?? 'All Tiers'}
								control={<CoreRadio />}
								value={DiscountTierEnum.All}
								sx={{
									'& .MuiFormControlLabel-label': {
										fontWeight: '500',
										fontSize: '15px',
										lineHeight: '21px',
									},
								}}
							/>
							<RadioFormLabel
								label={selectedTiersRadioLabel ?? 'Select Tiers'}
								control={<CoreRadio />}
								value={DiscountTierEnum.Selected}
								sx={{
									'& .MuiFormControlLabel-label': {
										fontWeight: '500',
										fontSize: '15px',
										lineHeight: '21px',
									},
								}}
							/>
						</CoreRadioGroup>

						<Showable show={tiersType === DiscountTierEnum.Selected}>
							<CoreConditionalSelect
								{...register(`discounts.${index}.tiers`, {
									ignoreOriginalOnChange: true,
									transformValue: (values: Record<string, boolean>) =>
										Object.keys(values ?? {}).filter((key) => values[key] === true && key !== 'all'),
									changePropKey: 'onValueSelected',
									onChange(values: string[]) {
										const newValue = values.reduce(
											(acc, option) => ({ ...acc, [option]: true }),
											{} as Record<string, boolean>
										);

										setValue(`discounts.${index}.tiers`, newValue);
									},
								})}
								controlId={`discounts.${index}.tiers`}
								multiple
								options={tiersOptions}
								placeholder="Select at least one tier"
								sx={defaultSelectInputStyles()}
							/>
						</Showable>

						<Box display='flex' gap='12px'>
							<CoreLabeledCheckbox
								value={withMaxUses}
								name={`discounts.${index}.withMaxUses`}
								onChange={(_, checked) => {
									setValue(`discounts.${index}.withMaxUses`, checked);

									if (!checked) {
										setValue(`discounts.${index}.maxUses`, null);
									}
								}}
								label={(
									<Box display="flex" alignItems="center" gap="4px">
										<Typography>Limit usage</Typography>
										<Tooltip
											title='Set a limit on the number of times the coupon can be used.'
											placement='top'
											componentsProps={{
												popper: {
													sx: {
														zIndex: 10000,
													},
												},
												tooltip: {
													sx: {
														bgcolor: '#222222',
														color: '#fff',
														fontSize: '14px',
														fontFamily: 'Montserrat',
														padding: '12px',
														boxSizing: 'border-box',
														width: 260,
														border: '1px solid #343434',
														borderBottom: 'none',
														borderRadius: '8px',
														textAlign: 'center',
													},
												},
											}}
											arrow
										>
											<Box width="20px" height="20px">
												<InfoIcon width="100%" height="100%"/>
											</Box>
										</Tooltip>
									</Box>
								)}
								labelSx={{
									fontSize: '14px',
									fontWeight: 600,
									lineHeight: '112%',

									'&.Mui-disabled': {
										color: Colors.white,
									},
								}}
								sx={{
									'&:hover': {
										backgroundColor: 'transparent',
									},

									'& .MuiTouchRipple-root': {
										display: 'none',
									},
								}}
							/>
							<Showable show={withMaxUses}>
								<CoreNumberInput
									{...(register(`discounts.${index}.maxUses`, {
										type: 'number',
										passRef: false,
										passType: false,
									}) as any)}
									onChange={value => {
										setValue(`discounts.${index}.maxUses`, value ?? null);
									}}
									decimalScale={0}
									allowNegative={false}
									lang='en-US'
									placeholder='Unlimited'
									startAdornment={<LimitIcon width={20} height={20} />}
									controlSx={{ flex: 1 }}
								/>
							</Showable>
						</Box>
					</Box>
				</Collapse>
			</Box>

			{isNumber(promocodeUses) && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '10px',
						padding: '8px',
						backgroundColor: Colors.white_a5,
						borderTop: `1px solid ${Colors.white_a5}`,
					}}
				>
					<Typography
						sx={{
							fontSize: '10px',
							fontWeight: 500,
							color: Colors.white,
							lineHeight: '12px',
							textTransform: 'uppercase',
							fontFamily: 'Montserrat',
						}}
					>
						Coupon used
					</Typography>
					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 600,
							color: isNumber(maxUses) && promocodeUses > maxUses ? '#F13939' : Colors.white,
							lineHeight: '21px',
							textTransform: 'uppercase',
							fontFamily: 'Montserrat',
						}}
					>
						{promocodeUses}
						<Box
							component={'span'}
							sx={{
								fontSize: '13px',
								fontWeight: 600,
								color: Colors.white_a70,
								lineHeight: '21px',
								textTransform: 'uppercase',
								fontFamily: 'Montserrat',
							}}
						>
							{` / ${maxUses ?? '∞'}`}
						</Box>
					</Typography>
					<Showable show={isNumber(maxUses) && promocodeUses > maxUses}>
						<Tooltip
							title='More codes were used due to a previous limit change'
							placement='top'
							componentsProps={{
								popper: {
									sx: {
										zIndex: 10000,
									},
								},
								tooltip: {
									sx: {
										bgcolor: '#222222',
										color: '#fff',
										fontSize: '14px',
										fontFamily: 'Montserrat',
										padding: '12px',
										boxSizing: 'border-box',
										width: 260,
										border: '1px solid #343434',
										borderBottom: 'none',
										borderRadius: '8px',
										textAlign: 'center',
									},
								},
							}}
							arrow
						>
							<IconButton sx={{ '&:hover': { backgroundColor: 'transparent' }, padding: '0' }}>
								<InfoIcon width={24} height={24}/>
							</IconButton>
						</Tooltip>
					</Showable>
				</Box>
			)}
		</Card>
	);
};
